import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy, inject } from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperInstance, ChartWrapperService } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { formatDecimalWithCommas } from '../../utils/format.utils';

interface SegmentPart {
  label: string;
  value: number;
  percent: number;
  color: string;
}

interface SegmentLegendItem {
  label: string;
  color: string;
}

interface SegmentDistributionRow {
  label: string;
  filterValue: string;
  total: number;
  segments: SegmentPart[];
}

interface SegmentParentGroup extends SegmentDistributionRow {
  children: SegmentDistributionRow[];
  items: SegmentDistributionRow[];
}

type StyleVarMap = Record<string, string>;

@Component({
  selector: 'app-horizontal-bar-chart-segment',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './horizontal-bar-chart-segment.component.html',
  styleUrl: './horizontal-bar-chart-segment.component.scss',
})
export class HorizontalBarChartSegmentComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;

  protected myChart: echarts.ECharts | null = null;
  protected allSeriesNames: string[] = [];
  protected allCategories: string[] = [];
  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();

  protected parentGroups: SegmentParentGroup[] = [];
  protected legendItems: SegmentLegendItem[] = [];
  protected rootStyleVars: StyleVarMap = {};
  protected isLoading = false;
  protected hasError = false;
  protected errorMessage = '';
  protected compactMode = false;

  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private reflowSub?: Subscription;

  ngAfterViewInit(): void {
    this.refreshVisualConfig();

    this.wrapper.attach(
      this.data,
      {
        onReload: (filters) => this.loadAndRenderChart(filters),
        getChart: () => this.myChart,
        getSeriesNames: () => this.allSeriesNames,
        getCategories: () => this.allCategories,
      },
      'category',
    );

    this.updateLayoutMode();
    this.loadAndRenderChart();

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.updateLayoutMode();
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';
    this.refreshVisualConfig();

    this.configService
      .getConfig(this.data.apiUrl, this.data, {
        ...(filters || {}),
        useQueryParams: filters?.useQueryParams ?? this.data?.useQueryParams ?? false,
      })
      ?.subscribe({
        next: (response: any) => {
          this.applyResponse(response);
        },
        error: () => {
          this.parentGroups = [];
          this.legendItems = [];
          this.allCategories = [];
          this.isLoading = false;
          this.hasError = true;
          this.errorMessage = 'No data available for the chart.';
        },
      });
  }

  protected handleCategoryClick(filterValue: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.data?.enableLabelClick || !filterValue) return;
    this.wrapper.handleLabelClick(filterValue);
  }

  protected isSelected(filterValue: string): boolean {
    return this.wrapper.selectedCategories.includes(filterValue);
  }

  protected isMuted(filterValue: string): boolean {
    return this.wrapper.selectedCategories.length > 0 && !this.isSelected(filterValue);
  }

  protected isScrollableRow(group: SegmentParentGroup): boolean {
    return this.compactMode || group.children.length > this.getVisibleChildCount();
  }

  protected shouldFillChildRow(group: SegmentParentGroup): boolean {
    return !this.isScrollableRow(group);
  }

  protected formatValue(value: number): string {
    return formatDecimalWithCommas(Number(value || 0));
  }

  protected formatPercent(value: number): string {
    return `${Number(value || 0).toFixed(1)}%`;
  }

  protected shouldShowPercent(value: number): boolean {
    return value >= Number(this.data?.chartConfig?.minPercentLabel ?? 12);
  }

  protected useDarkText(color: string): boolean {
    const normalized = String(color || '').trim();
    const hex = normalized.replace('#', '');
    if (!/^[0-9a-fA-F]{6}$/.test(hex)) return false;

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.68;
  }

  private applyResponse(response: any): void {
    const groups = this.resolveGroups(response);
    const chartConfig = this.data?.chartConfig || {};

    if (!groups.length) {
      this.parentGroups = [];
      this.legendItems = [];
      this.allSeriesNames = [];
      this.allCategories = [];
      this.isLoading = false;
      this.hasError = true;
      this.errorMessage = 'No grouped data was found in the response.';
      this.wrapper.afterRender();
      return;
    }

    this.parentGroups = groups.map((group: any, index: number) =>
      this.normalizeGroup(group, index, chartConfig),
    );
    this.legendItems = this.collectLegendItems(this.parentGroups);
    this.allSeriesNames = this.legendItems.map((item) => item.label);
    this.allCategories = this.parentGroups.flatMap((group) =>
      group.items.map((item) => item.filterValue),
    );

    this.isLoading = false;
    this.hasError = false;
    this.wrapper.afterRender();
  }

  private resolveGroups(response: any): any[] {
    const chartConfig = this.data?.chartConfig || {};
    const configuredPath =
      this.data?.dataPath ||
      this.data?.responsePath ||
      chartConfig.dataPath ||
      chartConfig.responsePath;
    const groupsKey = chartConfig.groupsKey || 'groups';

    const candidates = [
      configuredPath ? this.getByPath(response, configuredPath) : undefined,
      response,
      response?.data,
      Array.isArray(response?.data) ? response.data[0] : undefined,
      response?.responseList?.[0],
      response?.responseList?.[0]?.data,
      response?.responseList?.[0]?.data?.[0],
    ];

    for (const candidate of candidates) {
      if (!candidate) continue;
      if (Array.isArray(candidate?.[groupsKey])) return candidate[groupsKey];
      if (Array.isArray(candidate?.parentGroups)) return candidate.parentGroups;
      if (Array.isArray(candidate)) return candidate;
    }

    return [];
  }

  private normalizeGroup(group: any, index: number, chartConfig: any): SegmentParentGroup {
    const groupLabelKey = chartConfig.groupLabelKey || 'label';
    const groupValueKey = chartConfig.groupValueKey || 'filterValue';
    const childLabelKey = chartConfig.childLabelKey || 'label';
    const childValueKey = chartConfig.childValueKey || 'filterValue';
    const childrenKey = chartConfig.childrenKey || 'children';

    const label = this.readDisplayValue(group, groupLabelKey, `Group ${index + 1}`);
    const filterValue = this.readDisplayValue(group, groupValueKey, label);
    const children = Array.isArray(this.getByPath(group, childrenKey)) ? this.getByPath(group, childrenKey) : [];
    const parentRow = this.buildDistributionRow(group, label, filterValue, chartConfig);
    const childRows = children.map((child: any, childIndex: number) => {
      const childLabel = this.readDisplayValue(child, childLabelKey, `Item ${childIndex + 1}`);
      const defaultChildValue = `${filterValue}::${childLabel}`;
      const childFilterValue = this.readDisplayValue(child, childValueKey, defaultChildValue);
      return this.buildDistributionRow(child, childLabel, childFilterValue, chartConfig);
    });

    return {
      ...parentRow,
      children: childRows,
      items: [parentRow, ...childRows],
    };
  }

  private buildDistributionRow(
    source: any,
    label: string,
    filterValue: string,
    chartConfig: any,
  ): SegmentDistributionRow {
    const segments = this.normalizeSegments(source, chartConfig);
    const total = segments.reduce((sum, segment) => sum + segment.value, 0);
    let consumedPercent = 0;

    const normalizedSegments = segments.map((segment, index) => {
      let percent = 0;
      if (total > 0) {
        if (index === segments.length - 1) {
          percent = Number(Math.max(0, 100 - consumedPercent).toFixed(1));
        } else {
          percent = Number(((segment.value / total) * 100).toFixed(1));
          consumedPercent += percent;
        }
      }

      return {
        ...segment,
        percent,
      };
    });

    return {
      label,
      filterValue,
      total,
      segments: normalizedSegments,
    };
  }

  private normalizeSegments(source: any, chartConfig: any): SegmentPart[] {
    const segmentsKey = chartConfig.segmentsKey || 'segments';
    const segmentLabelKey = chartConfig.segmentLabelKey || 'label';
    const segmentValueKey = chartConfig.segmentValueKey || 'value';
    const segmentColorKey = chartConfig.segmentColorKey || 'color';
    const configuredColorMap = chartConfig.segmentColorsByLabel || {};
    const preferConfiguredColors = chartConfig.preferConfiguredSegmentColors !== false;
    const defaultColors = Array.isArray(chartConfig.defaultSegmentColors) && chartConfig.defaultSegmentColors.length
      ? chartConfig.defaultSegmentColors
      : ['#0f766e', '#cbd5e1', '#94a3b8', '#f59e0b'];

    const sourceSegments = Array.isArray(this.getByPath(source, segmentsKey))
      ? this.getByPath(source, segmentsKey)
      : [];

    return sourceSegments.map((segment: any, index: number) => {
      const label = this.readDisplayValue(segment, segmentLabelKey, `Segment ${index + 1}`);
      const value = this.readNumber(segment, segmentValueKey);
      const responseColor = this.readOptionalString(segment, segmentColorKey);
      const configuredColor = this.lookupConfiguredColor(configuredColorMap, label);
      const fallbackColor = defaultColors[index % defaultColors.length];

      if (configuredColor && preferConfiguredColors) {
        return {
          label,
          value,
          percent: 0,
          color: configuredColor,
        };
      }

      const resolvedBaseColor = preferConfiguredColors
        ? configuredColor || responseColor || fallbackColor
        : responseColor || configuredColor || fallbackColor;
      const color = this.wrapper.applyColorConsistency(label, resolvedBaseColor)
        || resolvedBaseColor
        || fallbackColor;

      return {
        label,
        value,
        percent: 0,
        color,
      };
    });
  }

  private collectLegendItems(groups: SegmentParentGroup[]): SegmentLegendItem[] {
    const seen = new Map<string, string>();

    groups.forEach((group) => {
      group.items.forEach((item) => {
        item.segments.forEach((segment) => {
          if (!seen.has(segment.label)) {
            seen.set(segment.label, segment.color);
          }
        });
      });
    });

    return Array.from(seen.entries()).map(([label, color]) => ({ label, color }));
  }

  private refreshVisualConfig(): void {
    const chartConfig = this.data?.chartConfig || {};
    const layout = chartConfig.layout || {};
    const styles = chartConfig.styles || {};

    this.rootStyleVars = {
      '--segment-parent-fixed-width': this.readCssValue(layout.parentWidth, '21rem'),
      '--segment-mobile-parent-fixed-width': this.readCssValue(layout.mobileParentWidth, '19rem'),
      '--segment-mobile-child-fixed-width': this.readCssValue(layout.mobileChildWidth, '13.5rem'),
      '--segment-child-gap': this.readCssValue(layout.rowGap, '0.85rem'),
      '--segment-visible-child-count': String(this.getVisibleChildCount()),
      '--segment-scroll-child-width': this.buildScrollChildWidth(),
      '--segment-parent-card-background': this.readCssValue(
        styles.parentCardBackground,
        'linear-gradient(180deg, rgba(236, 253, 245, 0.98) 0%, rgba(255, 255, 255, 0.94) 100%)',
      ),
      '--segment-parent-card-border': this.readCssValue(
        styles.parentCardBorder,
        'rgba(15, 118, 110, 0.34)',
      ),
      '--segment-parent-card-shadow': this.readCssValue(
        styles.parentCardShadow,
        '0 20px 36px rgba(15, 118, 110, 0.14)',
      ),
    };

    this.updateLayoutMode();
  }

  private readDisplayValue(source: any, path: string, fallback: string): string {
    const resolved = this.getByPath(source, path);
    if (resolved !== undefined && resolved !== null && `${resolved}`.trim() !== '') {
      return String(resolved);
    }
    return fallback;
  }

  private readOptionalString(source: any, path?: string): string | undefined {
    if (!path) return undefined;
    const resolved = this.getByPath(source, path);
    if (resolved !== undefined && resolved !== null && `${resolved}`.trim() !== '') {
      return String(resolved);
    }
    return undefined;
  }

  private lookupConfiguredColor(colorMap: Record<string, string>, label: string): string | undefined {
    if (!colorMap || !label) return undefined;

    if (typeof colorMap[label] === 'string' && colorMap[label].trim()) {
      return colorMap[label];
    }

    const lowerLabel = label.toLowerCase();
    const matchedEntry = Object.entries(colorMap).find(
      ([key, value]) => key.toLowerCase() === lowerLabel && `${value}`.trim() !== '',
    );

    return matchedEntry ? String(matchedEntry[1]) : undefined;
  }

  private getVisibleChildCount(): number {
    const configuredCount = Number(this.data?.chartConfig?.layout?.visibleChildCount);
    return Number.isFinite(configuredCount) && configuredCount > 0 ? configuredCount : 4;
  }

  private buildScrollChildWidth(): string {
    const visibleChildCount = this.getVisibleChildCount();
    const visibleGapCount = Math.max(visibleChildCount - 1, 0);
    return `calc((100% - (${visibleGapCount} * var(--segment-child-gap))) / ${visibleChildCount})`;
  }

  private readCssValue(value: unknown, fallback: string): string {
    if (value === undefined || value === null) return fallback;
    const normalized = String(value).trim();
    return normalized ? normalized : fallback;
  }

  private readNumber(source: any, path: string): number {
    const resolved = this.getByPath(source, path);
    return Number(resolved ?? 0) || 0;
  }

  private getByPath(source: any, path?: string): any {
    if (!source || !path) return source;

    return path.split('.').reduce((current: any, token: string) => {
      if (current == null) return undefined;
      if (/^\d+$/.test(token)) return current[Number(token)];
      return current[token];
    }, source);
  }

  private updateLayoutMode(): void {
    this.compactMode = window.innerWidth < 768;
  }

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
  }
}
